// app/javascript/controllers/chat_input_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "hidden", "button", "status", "peerTyping"]
  static values = {
    assistantName: String,
    submitting: Boolean,
    typingUrl: String,
    typingStatusUrl: String,
    typingPollMs: { type: Number, default: 2000 },
    typingIdleMs: { type: Number, default: 4500 }
  }

  connect() {
    this.typingActive = false
    this.typingIdleTimer = null
    this.typingHeartbeatTimer = null
    this.typingPollTimer = null

    if (this.hasTypingStatusUrlValue) {
      this.typingPollTimer = setInterval(() => this.pollTypingStatus(), this.typingPollMsValue)
      this.pollTypingStatus()
    }
  }

  disconnect() {
    this.clearPresenceTimers()
    this.clearTypingPollTimer()
    this.sendTyping("stop")
  }

  keydown(event) {
    // Enter submits, Shift+Enter inserts newline
    if (event.key !== "Enter" || event.shiftKey) return

    event.preventDefault()
    event.stopPropagation()

    if (this.submittingValue) return

    const form = event.target.closest("form")
    if (!form) return

    this.submittingValue = true
    this.sendTyping("stop")
    this.prepareSubmission()
    this.showThinkingUI()

    setTimeout(() => {
      form.submit()
    }, 0)
  }

  submit(event) {
    // Handles clicking the Send button (normal form submit)
    if (this.submittingValue) return

    event.preventDefault()
    event.stopPropagation()

    const form = this.element.closest("form") || this.element
    if (!form) return

    this.submittingValue = true
    this.sendTyping("stop")
    this.prepareSubmission()
    this.showThinkingUI()

    setTimeout(() => {
      form.submit()
    }, 0)
  }

  prepareSubmission() {
    // Ensure content is submitted even if textarea becomes disabled
    if (this.hasHiddenTarget && this.hasInputTarget) {
      this.hiddenTarget.value = this.inputTarget.value || ""
    }
  }

  showThinkingUI() {
    // Prevent further edits while submitting
    if (this.hasInputTarget) this.inputTarget.disabled = true

    // Hide the Send button entirely
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.add("d-none")
    }

    // Show the “Assistant is thinking…” status line
    if (this.hasStatusTarget) {
      this.statusTarget.classList.remove("d-none")
    }
  }

  inputChanged() {
    if (this.submittingValue) return
    if (!this.hasTypingUrlValue) return

    this.sendTyping("start")
    this.resetTypingIdleTimer()
    this.ensureTypingHeartbeat()
  }

  inputBlur() {
    this.sendTyping("stop")
    this.clearPresenceTimers()
  }

  async pollTypingStatus() {
    if (!this.hasTypingStatusUrlValue || !this.hasPeerTypingTarget) return

    try {
      const res = await fetch(this.typingStatusUrlValue, {
        method: "GET",
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) return

      const payload = await res.json()
      this.renderPeerTyping(payload.users || [])
    } catch (_) {
      // Best-effort UX only.
    }
  }

  renderPeerTyping(users) {
    if (!this.hasPeerTypingTarget) return
    if (!Array.isArray(users) || users.length === 0) {
      this.peerTypingTarget.textContent = ""
      this.peerTypingTarget.classList.add("d-none")
      return
    }

    const label = this.formatTypingLabel(users)
    this.peerTypingTarget.textContent = `${label} typing…`
    this.peerTypingTarget.classList.remove("d-none")
  }

  formatTypingLabel(users) {
    if (users.length === 1) return users[0]
    if (users.length === 2) return `${users[0]} and ${users[1]}`
    return `${users[0]}, ${users[1]} and others`
  }

  resetTypingIdleTimer() {
    if (this.typingIdleTimer) clearTimeout(this.typingIdleTimer)
    this.typingIdleTimer = setTimeout(() => {
      this.sendTyping("stop")
      this.clearPresenceTimers()
    }, this.typingIdleMsValue)
  }

  ensureTypingHeartbeat() {
    if (this.typingHeartbeatTimer) return
    this.typingHeartbeatTimer = setInterval(() => {
      if (!this.typingActive) return
      this.sendTyping("start", { force: true })
    }, 2000)
  }

  clearPresenceTimers() {
    if (this.typingIdleTimer) clearTimeout(this.typingIdleTimer)
    if (this.typingHeartbeatTimer) clearInterval(this.typingHeartbeatTimer)
    this.typingIdleTimer = null
    this.typingHeartbeatTimer = null
  }

  clearTypingPollTimer() {
    if (this.typingPollTimer) clearInterval(this.typingPollTimer)
    this.typingPollTimer = null
  }

  sendTyping(state, { force = false } = {}) {
    if (!this.hasTypingUrlValue) return

    if (state === "start" && this.typingActive && !force) return
    if (state === "stop" && !this.typingActive) return
    this.typingActive = state === "start"

    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    fetch(this.typingUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        ...(csrf ? { "X-CSRF-Token": csrf } : {})
      },
      credentials: "same-origin",
      keepalive: state === "stop",
      body: JSON.stringify({ state })
    }).catch(() => {
      // Non-critical presence signal.
    })
  }


  thinkingLabel() {
    const name = this.hasAssistantNameValue ? this.assistantNameValue : "Assistant"
    return `<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>${name} is thinking…`
  }
}
